import { useEffect, useMemo } from "react";
import { api } from "./api";

/**
 * Derive a consistent presence summary from the WebSocket presence map.
 *
 * A single deterministic client-side computation used by sidebar, home,
 * map, admin. Matches the server rules in ws_manager.py: `online` requires
 * a live session AND non-away, non-offline effective status.
 */
export function isOnline(p) { return !!(p && p.online && p.status !== "offline"); }
export function isMeeting(p) { return isOnline(p) && p.status === "meeting"; }
export function isAway(p) { return isOnline(p) && p.status === "away"; }

/**
 * Human-friendly last-seen formatter for management-grade visibility.
 * Returns null when the timestamp is unknown so callers can decide what
 * to render in the "never seen" case.
 *
 * Examples: "Just now" · "12m ago" · "Today 14:22" · "Yesterday 09:05"
 *           · "20 Feb, 14:22" · "3 Feb 2025, 10:11"
 */
export function formatLastSeen(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffSec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diffSec < 45) return "Just now";
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const seen = new Date(d); seen.setHours(0, 0, 0, 0);
  const hhmm = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (seen.getTime() === today.getTime()) return `Today ${hhmm}`;
  if (seen.getTime() === yesterday.getTime()) return `Yesterday ${hhmm}`;
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const dateStr = d.toLocaleDateString([], sameYear
    ? { day: "numeric", month: "short" }
    : { day: "numeric", month: "short", year: "numeric" });
  return `${dateStr}, ${hhmm}`;
}

/** Absolute timestamp in the user's locale (tooltip use). */
export function formatExact(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

export function computePresenceSummary(presenceMap, memberships) {
  const membershipUserIds = new Set((memberships || []).map((m) => m.user_id));
  let online = 0, meeting = 0, away = 0, offline = 0;
  const seen = new Set();
  const values = Object.values(presenceMap || {});
  for (const p of values) {
    if (!membershipUserIds.size || membershipUserIds.has(p.user_id)) {
      seen.add(p.user_id);
      if (!p.online) offline++;
      else if (p.status === "meeting") meeting++;
      else if (p.status === "away") away++;
      else online++;
    }
  }
  // members with no presence record at all = offline
  for (const uid of membershipUserIds) if (!seen.has(uid)) offline++;
  return { online, meeting, away, offline };
}

/** Convenience hook: recomputes when either map or membership list changes. */
export function usePresenceSummary(presenceMap, memberships) {
  return useMemo(() => computePresenceSummary(presenceMap, memberships), [presenceMap, memberships]);
}

/** Fetch server-authoritative summary as a fallback / verification path. */
export async function fetchServerPresenceSummary() {
  try {
    const { data } = await api.get("/presence/summary");
    return data.summary;
  } catch {
    return null;
  }
}
